import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  MessageCircle,
  MoreVertical,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  Ticket as TicketIcon,
  UserCheck,
  UserMinus,
} from 'lucide-react'
import {
  Fragment,
  useEffect,
  useState,
  type DragEvent,
  type ReactNode,
} from 'react'

import { templateHeaderFormat } from '@aeris/communication'
import {
  Avatar,
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  Spinner,
} from '@aeris/ui'

import {
  ApiError,
  assignConversation,
  createTicket,
  fetchConversationDetail,
  fetchMessages,
  setConversationTags,
  markConversationRead,
  sendConversationImage,
  sendConversationMessage,
  sendConversationTemplate,
  type ConversationSummaryType,
  type TemplateSummaryType,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { imageFileOf } from '../lib/clipboard-image.ts'
import {
  contactHasName,
  contactLabelOf,
  dateSeparatorFor,
  contactPhoneOf,
  contactSeedOf,
  inboxTagsOf,
  MESSAGES_PAGE_SIZE,
  REFRESH_INTERVAL_MS,
  useNow,
  windowStateFor,
} from '../lib/inbox.ts'
import { useQuickReplies } from '../lib/quick-replies.ts'
import { useContactNames } from '../lib/use-contact-names.ts'
import { useStickToBottom } from '../lib/use-stick-to-bottom.ts'
import { useWorkspace } from '../lib/workspace.ts'

import { ContactNameDialog } from './contact-name-dialog.tsx'
import { InboxCommerceActions } from './inbox-commerce-actions.tsx'
import {
  MessageComposer,
  ServiceWindowLine,
  type ComposerAttachment,
} from './inbox-composer.tsx'
import { DateChip, MessageBubble } from './inbox-message.tsx'
import { InboxTags } from './inbox-tags.tsx'
import { TemplateComposer } from './inbox-template-composer.tsx'
import { TagPicker } from './tag-picker.tsx'

/**
 * A conversa aberta — F085.
 *
 * ## O que mudou de forma
 *
 * Deixou de ser um cartão (`h-[34rem]`, borda, cantos, sombra) dentro de uma
 * página e passou a ser a REGIÃO CENTRAL do espaço de trabalho: ocupa a altura
 * inteira, é separada da lista por uma régua de 1px, e as três faixas dentro
 * dela — cabeçalho, mensagens, resposta — dividem o espaço em coluna flex,
 * onde só a do meio rola.
 *
 * ## O que continua igual, e por quê
 *
 * O envio, o tempo real, a janela de 24h, o modelo aprovado, o chamado e a
 * atribuição são exatamente o mesmo código de antes. A F085 é uma refatoração
 * de FORMA: um layout novo que quebra o envio não é um layout melhor.
 *
 * A fila e o cadastro do cliente continuam no painel da direita: são coisas
 * que se consulta, não que se opera a cada mensagem. O que subiu para o menu
 * `⋯` do cabeçalho foi a ATRIBUIÇÃO — "assumir esta conversa" é uma decisão
 * que se toma no meio de uma leitura, e ter de abrir um painel para isso é uma
 * fricção que o atendente paga dezenas de vezes por dia.
 */

export type InboxChatProps = {
  conversation: ConversationSummaryType
  /** Volta para a lista — só existe abaixo de `lg`, onde a lista foi substituída. */
  onBack: () => void
  /** Escondida abaixo de `lg` quando o painel de detalhes está ocupando a tela. */
  hiddenOnSmall: boolean
  live: boolean
  employeeNames: ReadonlyMap<string, string>
  templates: readonly TemplateSummaryType[]
  panelOpen: boolean
  onTogglePanel: () => void
  /**
   * Mostrar por qual número esta conversa chegou (F091).
   *
   * `false` quando a loja tem um número só, ou quando a pessoa já escolheu um
   * no seletor da lista — nos dois casos a resposta é a mesma para tudo que
   * está na tela, e o marcador gastaria uma das quatro vagas da fileira para
   * repetir o que não varia.
   */
  showChannel: boolean
}

export const InboxChat = ({
  conversation,
  onBack,
  hiddenOnSmall,
  live,
  employeeNames,
  templates,
  panelOpen,
  onTogglePanel,
  showChannel,
}: InboxChatProps): ReactNode => {
  const { allows, session } = useAuth()
  const canSend = allows('communication.message.send')
  const canAssign = allows('communication.conversation.assign')
  const canOpenTicket = allows('communication.ticket.write')
  const selfId = session?.user.id ?? null
  const queryClient = useQueryClient()
  const workspace = useWorkspace()
  const now = useNow(60_000)

  const [draft, setDraft] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [templateError, setTemplateError] = useState<string | null>(null)

  /*
   * Aparelho vinculado (F192) não tem janela de 24 h nem modelo: é o próprio
   * celular da loja, que responde quando quiser. Sem a linha da janela e sem
   * o compositor de modelo — mostrar "janela fechada" ali seria mentir.
   */
  const linkedDevice = conversation.accountConnection === 'linked_device'
  const windowState = windowStateFor(conversation.lastInboundAt, now)
  const freeform = linkedDevice || windowState.open

  /*
   * A imagem esperando para sair (03/10): colada, anexada ou arrastada. Só
   * pelo aparelho vinculado — o número oficial ainda não manda anexo.
   */
  const [attachment, setAttachment] = useState<ComposerAttachment | null>(null)
  const [attachError, setAttachError] = useState<string | null>(null)
  const [dragging, setDragging] = useState(false)
  const canAttach = linkedDevice && canSend

  useEffect(
    () => () => {
      if (attachment !== null) URL.revokeObjectURL(attachment.previewUrl)
    },
    [attachment]
  )

  const attach = (file: File): void => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setAttachError('Só dá para mandar imagem JPEG, PNG ou WebP.')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      setAttachError('A imagem deve ter no máximo 10 MB.')
      return
    }
    setAttachError(null)
    setAttachment({ file, previewUrl: URL.createObjectURL(file) })
  }

  /*
   * Os marcadores ABREM o painel; nunca o fecham. Clicar em "Sem cadastro" com
   * o painel já aberto e vê-lo fechar seria o contrário do que o marcador
   * promete.
   */
  const openPanel = (): void => {
    if (!panelOpen) onTogglePanel()
  }

  const messagesQuery = useQuery({
    queryKey: ['communication-messages', conversation.id],
    queryFn: async ({ signal }) =>
      await fetchMessages(
        conversation.id,
        { limit: MESSAGES_PAGE_SIZE, offset: 0 },
        signal
      ),
    // `false` enquanto o SSE está de pé — ver `useCommunicationRealtime`.
    refetchInterval: live ? false : REFRESH_INTERVAL_MS,
  })

  /*
   * O cadastro por trás do número — para os marcadores do cabeçalho (F087).
   *
   * **A MESMA `queryKey` que `CustomerPanel` usa.** Não é coincidência: com o
   * painel aberto, o TanStack serve os dois consumidores com uma requisição
   * só; com ele fechado, esta é a única, e o cabeçalho deixa de depender de o
   * atendente ter aberto o painel para saber que o cliente tem título vencido.
   *
   * O atraso e o número de compras vêm daqui, e não da lista, porque são
   * cálculos dos domínios FINANCEIRO e de VENDAS — `creditStandingOf` e
   * `salesRepository.list`, pelas portas que a rota de detalhe já atravessa.
   * Trazê-los na listagem exigiria o módulo de atendimento ler tabelas de
   * outros dois módulos por baixo, que é exatamente o acoplamento que o
   * CLAUDE.md proíbe — e ainda por linha da página.
   */
  const detailQuery = useQuery({
    queryKey: ['communication-conversation-detail', conversation.id],
    queryFn: async ({ signal }) =>
      await fetchConversationDetail(conversation.id, signal),
  })

  /*
   * Abrir a conversa é lê-la — F084.
   *
   * A dependência inclui `unreadCount` de propósito, e não só o id: com a
   * conversa ABERTA na tela, uma mensagem que chega pelo tempo real também
   * conta como lida — quem está olhando acabou de ver. Converge sozinho, porque
   * a invalidação traz o contador zerado e o efeito para de disparar.
   *
   * O erro é engolido: falhar em marcar como lida não pode virar um alerta na
   * frente de quem está no meio de um atendimento.
   */
  const unreadCount = conversation.unreadCount
  useEffect(() => {
    if (unreadCount <= 0) return

    let cancelled = false
    void markConversationRead(conversation.id)
      .then(async () => {
        if (cancelled) return
        await queryClient.invalidateQueries({
          queryKey: ['communication-conversations'],
        })
      })
      .catch(() => {
        /* ver o comentário acima */
      })

    return () => {
      cancelled = true
    }
  }, [conversation.id, unreadCount, queryClient])

  const invalidateBoth = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['communication-conversations'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['communication-messages', conversation.id],
      }),
    ])
  }

  const assign = useMutation({
    mutationFn: async (employeeId: string | null) =>
      await assignConversation(conversation.id, { employeeId }),
    onSuccess: async () => {
      setActionError(null)
      await invalidateBoth()
    },
    onError: (cause: unknown) => {
      setActionError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível atribuir esta conversa.'
      )
    },
  })

  // Abrir chamado (F051): a rota herda `partyId` sozinha a partir de
  // `conversationId`, então esta tela só manda o que ela sabe.
  const openTicket = useMutation({
    mutationFn: async () =>
      await createTicket({
        subject: `Chamado a partir da conversa com ${contactLabelOf(conversation)}`,
        description: null,
        priority: 'normal',
        partyId: null,
        conversationId: conversation.id,
      }),
    onSuccess: () => {
      workspace.open({ module: 'chamados' })
    },
  })

  const sendMessage = useMutation({
    // O erro aparece na própria tela; o aviso geral não repete (F248).

    meta: { errorShownInline: true },
    mutationFn: async (input: {
      body: string
      image: ComposerAttachment | null
    }) =>
      input.image === null
        ? await sendConversationMessage(conversation.id, { body: input.body })
        : await sendConversationImage(conversation.id, {
            file: input.image.file,
            caption: input.body,
          }),
    onSuccess: async () => {
      setDraft('')
      setAttachment(null)
      await invalidateBoth()
    },
  })

  const sendTemplate = useMutation({
    mutationFn: async (input: { templateId: string; values: string[] }) =>
      await sendConversationTemplate(conversation.id, input),
    onSuccess: async () => {
      setTemplateError(null)
      await invalidateBoth()
    },
    onError: (cause: unknown) => {
      setTemplateError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível enviar o modelo.'
      )
    },
  })

  const isMine = selfId !== null && conversation.assigneeEmployeeId === selfId

  /**
   * Só modelos `approved` DESTA conta: enviar um `pending`/`rejected` é a mesma
   * recusa que `canSendTemplate` já faz no servidor, e oferecer a opção seria
   * só adiar o "não" para depois do clique. Um modelo com cabeçalho de MÍDIA
   * também fica de fora (F066), pelo mesmo motivo.
   */
  const approvedTemplates = templates.filter(
    (template) =>
      template.accountId === conversation.accountId &&
      template.status === 'approved' &&
      templateHeaderFormat(template.structure) === 'TEXT'
  )

  // A API devolve mais recente primeiro (a mesma leitura que a lista usa); a
  // thread mostra cronológico — mais antiga em cima, mais nova embaixo.
  const orderedMessages = [...(messagesQuery.data?.messages ?? [])].reverse()
  const logRef = useStickToBottom<HTMLDivElement>(
    conversation.id,
    `${String(messagesQuery.isPending)}:${String(orderedMessages.length)}:${orderedMessages.at(-1)?.id ?? ''}`
  )

  /*
   * O nome que ESTE atendente deu ao contato (01/10). Vale no cabeçalho e na
   * lista; o chamado e o resto do sistema continuam com o nome de sempre.
   */
  const contactNames = useContactNames()
  const ownName = contactNames.nameOf(conversation.contactId)
  // Os atalhos da vendedora (F297), também no "/" do campo.
  const quickReplies = useQuickReplies()
  const [renaming, setRenaming] = useState(false)
  const label = contactLabelOf(conversation, ownName)
  const usualLabel = contactLabelOf(conversation)
  const tags = inboxTagsOf(
    conversation,
    detailQuery.data?.party,
    showChannel ? conversation.accountDisplayName : null
  )

  /*
   * Marcar a CONVERSA — F088.
   *
   * A permissão é a mesma de atribuir: colar "Aguardando pagamento" é
   * organizar o atendimento, do mesmo tamanho que decidir quem responde.
   *
   * Sem `version` e sem otimismo: o servidor devolve a conversa inteira, e a
   * invalidação traz a lista com os marcadores novos. Um estado local aqui
   * discordaria do tempo real na primeira mensagem que chegasse no meio.
   */
  const saveTags = useMutation({
    mutationFn: async (tagIds: string[]) =>
      await setConversationTags(conversation.id, tagIds),
    onSuccess: async () => {
      setActionError(null)
      await queryClient.invalidateQueries({
        queryKey: ['communication-conversations'],
      })
    },
    onError: (cause) => {
      setActionError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível salvar os marcadores desta conversa.'
      )
    },
  })

  return (
    <section
      aria-label="Conversa"
      className={cn(
        'relative min-h-0 min-w-0 flex-1 flex-col bg-(--surface)',
        'lg:flex',
        hiddenOnSmall ? 'hidden' : 'flex'
      )}
      /*
       * Arrastar uma imagem para a conversa anexa (03/10) — a conversa
       * inteira é o alvo, não só o campo, que é onde ninguém mira.
       */
      {...(canAttach && freeform
        ? {
            onDragOver: (event: DragEvent<HTMLElement>) => {
              if (!Array.from(event.dataTransfer.types).includes('Files'))
                return
              event.preventDefault()
              setDragging(true)
            },
            onDragLeave: (event: DragEvent<HTMLElement>) => {
              if (event.currentTarget.contains(event.relatedTarget as Node))
                return
              setDragging(false)
            },
            onDrop: (event: DragEvent<HTMLElement>) => {
              event.preventDefault()
              setDragging(false)
              const file = imageFileOf(
                event.dataTransfer.items,
                event.dataTransfer.files
              )
              if (file === null) {
                setAttachError('Só dá para mandar imagem JPEG, PNG ou WebP.')
                return
              }
              attach(file)
            },
          }
        : {})}
    >
      {dragging ? (
        <div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-(--radius-lg) border-2 border-dashed border-(--brand-primary) bg-(--brand-primary-subtle) text-(length:--text-md) font-(--weight-medium) text-(--brand-primary)">
          Solte a imagem para anexar
        </div>
      ) : null}
      <header className="flex h-17 min-w-0 shrink-0 items-center gap-2.5 border-b border-(--border-default) px-3">
        {/*
         * Abaixo de `lg` a lista foi substituída por esta conversa, então
         * precisa haver um caminho de volta. Acima, a lista está ao lado e o
         * botão seria um controle que não leva a lugar nenhum.
         */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Voltar para as conversas"
          className="lg:hidden"
          onClick={onBack}
        >
          <ArrowLeft aria-hidden="true" className="h-4 w-4" />
        </Button>

        <Avatar label={label} seed={contactSeedOf(conversation)} size="md" />

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate text-(length:--text-md) font-(--weight-semibold) text-(--text-primary)">
              {contactHasName(conversation, ownName)
                ? label
                : contactPhoneOf(conversation)}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Editar o nome do contato só para você"
              title="Editar o nome só para você"
              className="shrink-0"
              onClick={() => {
                setRenaming(true)
              }}
            >
              <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            </Button>
          </span>
          {/*
           * A segunda linha só existe quando a primeira é um NOME (F084).
           * Sem cadastro e sem nome de perfil, o rótulo de cima JÁ é o número —
           * e a linha de baixo repetia o mesmo número, em fonte menor, como se
           * fosse outra informação: *"554299959582 / 554299959582 / aq tbm"*.
           */}
          {/*
           * A segunda linha carrega o telefone E os marcadores (F087).
           *
           * Elas ficam aqui, e não numa faixa própria, porque o cabeçalho tem
           * altura fixa e uma terceira linha empurraria a conversa para baixo
           * — foi exatamente o que o dono pediu para tirar na F085. E é onde
           * ele apontou: *"do lado do Thiago Leblanc +55 42 9854-3579"*.
           *
           * Escondidas abaixo de `sm`: ali o cabeçalho ainda carrega o botão
           * de voltar, e o nome — que é a informação principal — ficaria com
           * pouco mais de cem pixels.
           */}
          <span className="flex min-w-0 items-center gap-2">
            <span className="shrink-0 truncate text-(length:--text-xs) text-(--text-muted)">
              {contactHasName(conversation, ownName)
                ? ownName === null
                  ? contactPhoneOf(conversation)
                  : // O nome de sempre ao lado, para o apelido não esconder
                    // quem é a pessoa no cadastro.
                    `${usualLabel === contactPhoneOf(conversation) ? '' : `${usualLabel} · `}${contactPhoneOf(conversation)}`
                : 'Cliente não identificado'}
            </span>
            <InboxTags
              tags={tags}
              partyId={conversation.contactPartyId}
              onOpenPanel={openPanel}
              className="hidden sm:flex"
            />
          </span>
        </span>

        {canAssign ? (
          <TagPicker
            scope="conversation"
            compact
            value={conversation.tags.map((tag) => tag.id)}
            disabled={saveTags.isPending}
            emptyLabel="Marcadores desta conversa"
            className="shrink-0"
            onChange={(tagIds) => {
              saveTags.mutate(tagIds)
            }}
          />
        ) : null}

        {/*
         * O botão que devolve a largura à conversa (F083). `aria-expanded`
         * porque ele controla uma região que aparece e some — sem isso quem usa
         * leitor de tela não sabe em que estado está antes de clicar.
         */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={panelOpen}
          aria-controls="inbox-side-panel"
          onClick={onTogglePanel}
        >
          {panelOpen ? (
            <PanelRightClose aria-hidden="true" className="h-4 w-4" />
          ) : (
            <PanelRightOpen aria-hidden="true" className="h-4 w-4" />
          )}
          <span className="hidden sm:inline">
            {panelOpen ? 'Ocultar detalhes' : 'Detalhes'}
          </span>
        </Button>

        {/* Permissão só esconde os controles — a API decide de verdade. */}
        {canAssign || canOpenTicket ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Mais ações desta conversa"
              >
                <MoreVertical aria-hidden="true" className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canAssign ? (
                conversation.assigneeEmployeeId === null ? (
                  <DropdownMenuItem
                    disabled={selfId === null}
                    onSelect={() => {
                      assign.mutate(selfId)
                    }}
                  >
                    <UserCheck aria-hidden="true" className="h-4 w-4" />
                    Assumir esta conversa
                  </DropdownMenuItem>
                ) : isMine ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      assign.mutate(null)
                    }}
                  >
                    <UserMinus aria-hidden="true" className="h-4 w-4" />
                    Devolver para a fila
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem
                    disabled={selfId === null}
                    onSelect={() => {
                      assign.mutate(selfId)
                    }}
                  >
                    <UserCheck aria-hidden="true" className="h-4 w-4" />
                    Tomar de{' '}
                    {employeeNames.get(conversation.assigneeEmployeeId) ??
                      'outro atendente'}
                  </DropdownMenuItem>
                )
              ) : null}

              {canAssign && canOpenTicket ? <DropdownMenuSeparator /> : null}

              {canOpenTicket ? (
                <DropdownMenuItem
                  disabled={openTicket.isPending}
                  onSelect={() => {
                    openTicket.mutate()
                  }}
                >
                  <TicketIcon aria-hidden="true" className="h-4 w-4" />
                  Abrir chamado
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </header>

      {actionError === null ? null : (
        <p
          role="alert"
          className="shrink-0 border-b border-(--danger)/30 bg-(--danger-subtle) px-3 py-1.5 text-(length:--text-xs) text-(--text-primary)"
        >
          {actionError}
        </p>
      )}

      {/*
       * Dois elementos, e não um: o de fora é o quadro parado que segura a
       * textura, o de dentro é o que rola. Quando eram a mesma caixa, a textura
       * cobria só a primeira tela e ainda passava por cima das bolhas — o
       * porquê está em `.aeris-chat-surface`, no app.css.
       */}
      <div className="aeris-chat-surface flex min-h-0 flex-1 flex-col">
        <div
          ref={logRef}
          role="log"
          aria-label={`Mensagens com ${label}`}
          tabIndex={0}
          className="relative z-10 min-h-0 flex-1 overflow-y-auto px-4 py-3"
        >
          {messagesQuery.isPending ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : messagesQuery.isError ? (
            <EmptyState
              icon={<MessageCircle aria-hidden="true" className="h-8 w-8" />}
              title="Não foi possível carregar as mensagens"
              description="Verifique a conexão com o servidor e tente novamente."
            />
          ) : orderedMessages.length === 0 ? (
            <EmptyState
              icon={<MessageCircle aria-hidden="true" className="h-8 w-8" />}
              title="Nenhuma mensagem nesta conversa"
            />
          ) : (
            <ul className="flex flex-col gap-1.5">
              {orderedMessages.map((message, position) => {
                const separator = dateSeparatorFor(
                  message,
                  orderedMessages[position - 1]
                )
                return (
                  <Fragment key={message.id}>
                    {separator === null ? null : <DateChip label={separator} />}
                    <MessageBubble message={message} />
                  </Fragment>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="shrink-0">
        {linkedDevice ? null : (
          <ServiceWindowLine state={windowState} now={now} />
        )}

        {/* Permissão só esconde o controle — a API recusa por conta própria. */}
        {canSend && freeform ? (
          <InboxCommerceActions
            conversationId={conversation.id}
            branchId={session?.context?.branchId ?? undefined}
            disabled={sendMessage.isPending}
            quickReplies={{
              customerName: contactHasName(conversation, ownName)
                ? label
                : null,
              onSend: (text) => {
                if (text !== '') sendMessage.mutate({ body: text, image: null })
              },
            }}
            onInsert={(text) => {
              setDraft((current) =>
                current.trim() === '' ? text : `${current.trimEnd()}\n\n${text}`
              )
            }}
          />
        ) : null}
        {canSend && freeform ? (
          <MessageComposer
            value={draft}
            onChange={setDraft}
            onSubmit={() => {
              sendMessage.mutate({ body: draft.trim(), image: attachment })
            }}
            disabled={sendMessage.isPending}
            sending={sendMessage.isPending}
            quickReplies={{
              items: quickReplies.items,
              customerName: contactHasName(conversation, ownName)
                ? label
                : null,
            }}
            {...(canAttach
              ? {
                  attachment,
                  onAttach: attach,
                  onRemoveAttachment: () => {
                    setAttachment(null)
                  },
                }
              : {})}
          />
        ) : null}

        {canSend && !freeform ? (
          <TemplateComposer
            templates={approvedTemplates}
            now={now}
            sending={sendTemplate.isPending}
            error={templateError}
            onSend={(input) => {
              sendTemplate.mutate(input)
            }}
          />
        ) : null}

        {attachError === null ? null : (
          <p
            role="alert"
            className={cn(
              'border-t border-(--danger)/30 bg-(--danger-subtle)',
              'px-3 py-1.5 text-(length:--text-xs) text-(--text-primary)'
            )}
          >
            {attachError}
          </p>
        )}

        {sendMessage.isError ? (
          <p
            role="alert"
            className={cn(
              'border-t border-(--danger)/30 bg-(--danger-subtle)',
              'px-3 py-1.5 text-(length:--text-xs) text-(--text-primary)'
            )}
          >
            {sendMessage.error instanceof ApiError
              ? sendMessage.error.message
              : 'Não foi possível enviar a mensagem.'}
          </p>
        ) : null}
      </div>
      {renaming ? (
        <ContactNameDialog
          current={ownName}
          original={usualLabel}
          onSave={(name) => {
            contactNames.rename(conversation.contactId, name)
          }}
          onClose={() => {
            setRenaming(false)
          }}
        />
      ) : null}
    </section>
  )
}
